import type { Dict } from "../core.js";
import type { credentialHistory as vi } from "../vi/credentialHistory.js";

export const credentialHistory: Dict<typeof vi> = {
  title: "Version history",
  subtitle: "Set « {name} » · {product} · {env}",
  intro:
    "Every save, restore or delete keeps the previous version (it cannot be edited or deleted). Restoring writes a NEW version that copies the old content — history is never lost. Secret fields never show their value, only a hint of the last few characters.",
  back: "← Integrations",
  loading: "Loading…",
  notFound: "This credential set was not found (it may have been deleted).",
  loadFailed: "Could not load the history: {error}",
  readOnlyRole: "You have read-only access — you cannot restore versions or cut the grace period.",
  reload: "Reload",

  versionsTitle: "Versions",
  colRevision: "Version",
  colAuthor: "Written by",
  colTest: "Test run",
  colReplaced: "Replaced by",
  colSecrets: "Secret fields changed",
  colInbound: "Partner pushes",
  current: "current",
  unknownAuthor: "unknown",
  "kind.update": "Edit",
  "kind.restore": "Restore #{from}",
  "kind.delete": "Set deleted",
  note: "« {note} »",
  redacted: "content redacted",
  "inbound.current": "In use",
  "inbound.grace": "Still accepted until {when}",
  "inbound.capped": "Beyond the {max}-version cap — not accepted",
  "inbound.expired": "Grace period over",
  "inbound.cut": "Grace period cut",
  "inbound.none": "—",
  inboundNoneTitle: "The change that replaced it kept every secret field, so there is no old key to accept.",

  bindingsTitle: "Bound to",
  bindingsNone: "Not bound to any app.",
  bindingOn: "on",
  bindingOff: "off",
  bindingHistoryLater: "Binding history (which app used which set, since when) comes in phase 2c of HQ-2026-013.",

  compareTitle: "Compare two versions",
  compareFrom: "From",
  compareTo: "To",
  compareRun: "Compare",
  comparing: "Comparing…",
  compareFailed: "Could not compare: {error}",
  compareField: "Field",
  compareOfficial: "official catalog value",
  compareNoHistory: "This set has a single version — nothing to compare yet.",
  compareSameRevision: "Pick two different versions.",
  "same.true": "Same",
  "same.false": "Changed",
  "same.null": "Cannot compare",
  sameNullTitle: "The hub lacks the decryption key or the ciphertext is broken — it cannot tell whether the two values match.",
  changed: "changed",

  restore: "Restore #{n}",
  restoreTitle: "Restore version #{n}",
  restoreNext: "Set « {name} » gets a NEW version #{next} copying the content of #{n}. The current version #{current} stays in the history.",
  restoreApps: "ON for: {apps} ({env}) — apps use the new content from their next config read (apps that pull config: ≤ 60 seconds).",
  restoreNoApps: "Not enabled for any app — no effect on outgoing calls or inbound pushes yet.",
  restoreUntested: "The set goes back to « Untested »; enabled bindings stay enabled. Run a test right after restoring.",
  restoreGrace: "If the restore changes a secret field, the key of the current version #{current} is still accepted for partner pushes for another {hours} hours.",
  restoreWaive: "Do not keep a grace period for the current key (it has leaked)",
  reasonLabel: "Reason (required, 5–500 characters)",
  reasonPlaceholder: "e.g. « Pasted the wrong Secret Key at 18:18 »",
  confirmRestore: "Restore",
  restoring: "Restoring…",
  restored: "Restored — the current version is #{n}.",
  cancel: "Cancel",
  failed: "Could not complete the action.",

  versionAuthor: "{by} · {when}",
  summariesFailed: "Could not load version numbers / grace periods — saving still works, but concurrent edits are not checked and no automatic test runs after saving.",
  historyLink: "History",
  graceBanner: "The old key of version {revisions} is still sent to the apps bound to this set (only to verify partner pushes) until {when}.",
  cutGrace: "Cut grace period now",
  cutGraceTitle: "Cut the grace period — apps stop accepting EVERY old key of this set",
  cutGraceHint: "Use this when an old key has leaked. The hub pushes config immediately; apps that pull config stop accepting old keys within ≤ 60 seconds. Cannot be undone.",
  confirmCut: "Cut grace period",
  cutting: "Cutting…",

  editGraceNotice:
    "The new key applies IMMEDIATELY to outgoing calls. The old key is still accepted for partner pushes for another {hours} hours — change the key in the partner portal AFTER saving here. The set goes back to « Untested ».",
  waiveGrace: "The old key has leaked — no grace period (apps stop accepting the old key now)",
  noteLabel: "Note for the history (optional)",
  notePlaceholder: "e.g. « Rotated the Secret Key at Tingee's request »",
  autoTestNotice: "This set is ON for an app in live — after saving, the screen runs a test of the new key automatically.",
  conflictHint: "The set was just saved as version #{n}. « Reload » reopens the form with the latest content — re-enter what you typed (keys included).",

  autoTesting: "Testing the new key…",
  autoTestPassed: "The new key PASSED its test run.",
  autoTestFailed: "The new key FAILED its test run — apps use this key for outgoing calls. The old key (version #{n}) is still accepted for partner pushes until {when}.",
  autoTestFailedNoGrace: "The new key FAILED its test run — apps use this key. Restore version #{n} if the wrong key was pasted.",
  autoTestError: "Could not run the automatic test: {error}. Click « Run test » to try again.",
  autoRestoreNote: "New key failed its test run — back to version #{n}",
  dismiss: "Dismiss",
};
